import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getV1CheckpointResponsesOptions,
  getV1CheckpointResponsesQueryKey,
  patchV1CheckpointResponsesByIdMutation,
  postV1CheckpointResponsesMutation,
  postV1CheckpointResponsesUploadUrlsMutation,
} from '../client/@tanstack/react-query.gen'

export const useCheckpointResponses = (
  workOrderId: string,
  workflowStateId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    ...getV1CheckpointResponsesOptions({
      query: { workOrderId, workflowStateId, limit: 0, page: 1 },
    }),
    enabled: !!workOrderId && !!workflowStateId && (options?.enabled ?? true),
  })

// Every checkpoint response for a work order, across all workflow states — the
// `workflowStateId` filter is omitted so this returns the full history in one call.
export const useWorkOrderCheckpointResponses = (
  workOrderId: string,
  options?: { enabled?: boolean },
) => {
  const query = useQuery({
    ...getV1CheckpointResponsesOptions({ query: { workOrderId, limit: 0, page: 1 } }),
    enabled: !!workOrderId && (options?.enabled ?? true),
  })

  return { ...query, data: query.data?.docs ?? [] }
}

export const useCreateCheckpointResponses = () => {
  const queryClient = useQueryClient()

  return useMutation({
    ...postV1CheckpointResponsesMutation(),
    onSettled: (_response, _error, options) => {
      queryClient.invalidateQueries({
        queryKey: getV1CheckpointResponsesQueryKey({
          query: {
            workOrderId: options.body.workOrder,
            workflowStateId: options.body.workflowState,
            limit: 0,
            page: 1,
          },
        }),
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: getV1CheckpointResponsesQueryKey({
          query: { workOrderId: options.body.workOrder, limit: 0, page: 1 },
        }),
        exact: false,
      })
    },
  })
}

export const useUpdateCheckpointResponses = () => {
  const queryClient = useQueryClient()

  return useMutation({
    ...patchV1CheckpointResponsesByIdMutation(),
    onSettled: response => {
      if (!response) return
      queryClient.invalidateQueries({
        queryKey: getV1CheckpointResponsesQueryKey({
          query: {
            workOrderId: response.workOrder,
            workflowStateId: response.workflowState,
            limit: 0,
            page: 1,
          },
        }),
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: getV1CheckpointResponsesQueryKey({
          query: { workOrderId: response.workOrder, limit: 0, page: 1 },
        }),
        exact: false,
      })
    },
  })
}

export const useGenerateCheckpointUploadUrls = () =>
  useMutation(postV1CheckpointResponsesUploadUrlsMutation())
